import React, { useEffect, useState } from "react";
import "./StudentPortal.css";

export default function StudentPortal({ student, announcements = [], grades = [], schedule = [], onExportSOA }) {
  if (!student) {
    return <div className="container mt-5">Please log in to view your portal.</div>;
  }

  const campusLC = String(student.campus || "").toLowerCase();
  const themeClass = campusLC.includes("gapan")
    ? "theme-gapan"
    : campusLC.includes("bulacan")
    ? "theme-bulacan"
    : "theme-default";

  const [activeGradebook, setActiveGradebook] = useState({ url: "", subject: "" });

  const normKey = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const toEmbedUrl = (url) => {
    const u = String(url || "").trim();
    if (!u) return "";
    if (u.includes("docs.google.com") && u.includes("/edit")) return u.replace(/\/edit(\?.*)?(#.*)?$/, "/preview");
    if (u.includes("docs.google.com") && u.includes("/view")) return u.replace(/\/view(\?.*)?(#.*)?$/, "/preview");
    return u;
  };

  const closeGradebook = () => setActiveGradebook({ url: "", subject: "" });
  const modalOpen = Boolean(String(activeGradebook.url || "").trim());

  useEffect(() => {
    if (!modalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeGradebook();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [modalOpen]);

  const gradesBySubject = new Map();
  (Array.isArray(grades) ? grades : []).forEach(g => {
    const subject = String(g?.subject || g?.name || "").trim();
    const k = normKey(subject);
    if (!k) return;
    const cur = gradesBySubject.get(k);
    const curKey = String(cur?.updatedAt || cur?.createdAt || cur?.term || "");
    const nextKey = String(g?.updatedAt || g?.createdAt || g?.term || "");
    if (!cur || nextKey > curKey) gradesBySubject.set(k, { subject, grade: g?.grade, term: g?.term, updatedAt: g?.updatedAt, createdAt: g?.createdAt });
  });
  const scheduleSubjects = new Map();
  (Array.isArray(schedule) ? schedule : []).forEach(r => {
    const subject = String(r?.subject || r?.name || "").trim();
    const k = normKey(subject);
    if (!k) return;
    const url = String(r?.gradebookUrl || r?.gradebookLink || r?.link || "").trim();
    const prev = scheduleSubjects.get(k);
    if (!prev) {
      scheduleSubjects.set(k, { subject, gradebookUrl: url });
      return;
    }
    if (!String(prev.gradebookUrl || "").trim() && url) scheduleSubjects.set(k, { subject: prev.subject || subject, gradebookUrl: url });
  });
  const subjectRows = (() => {
    const rows = [];
    if (scheduleSubjects.size > 0) {
      scheduleSubjects.forEach((v, k) => {
        rows.push({ subject: v.subject, grade: gradesBySubject.get(k)?.grade, gradebookUrl: v.gradebookUrl });
      });
      gradesBySubject.forEach((g, k) => {
        if (scheduleSubjects.has(k)) return;
        rows.push({ subject: g.subject, grade: g.grade });
      });
      rows.sort((a, b) => String(a.subject || "").localeCompare(String(b.subject || "")));
      return rows;
    }
    (Array.isArray(grades) ? grades : [])
      .map(g => ({ subject: String(g?.subject || g?.name || "").trim(), grade: g?.grade }))
      .filter(r => r.subject)
      .forEach(r => {
        const k = normKey(r.subject);
        if (!k) return;
        if (!rows.some(x => normKey(x.subject) === k)) rows.push(r);
      });
    rows.sort((a, b) => String(a.subject || "").localeCompare(String(b.subject || "")));
    return rows;
  })();

  const scheduleRows = (() => {
    const rows = Array.isArray(schedule) ? schedule : [];
    const uniq = new Map();
    rows.forEach(r => {
      const day = String(r?.day || "").trim();
      const start = String(r?.start || "").trim();
      const end = String(r?.end || "").trim();
      const subject = String(r?.subject || r?.name || "").trim();
      const teacher = String(r?.teacher || "").trim();
      const room = String(r?.room || "").trim();
      const k = [normKey(day), normKey(start), normKey(end), normKey(subject), normKey(teacher), normKey(room)].join("|");
      if (!k) return;
      if (!uniq.has(k)) uniq.set(k, { ...r, day, start, end, subject, teacher, room });
    });
    return Array.from(uniq.values()).sort(
      (a, b) =>
        String(a.day || "").localeCompare(String(b.day || "")) ||
        String(a.start || "").localeCompare(String(b.start || ""))
    );
  })();

  return (
    <div className={`portal-wrap ${themeClass}`}>
      <div className="portal-header">
        <div className="container">
          <div className="d-flex align-items-center gap-3">
            <img src={student.photoUrl || "/vmsc_logo.png"} alt="Student" className="portal-avatar" />
            <div>
              <div className="portal-title">VMSC Student Portal</div>
              <div className="portal-subtitle">{student.name}</div>
              <div className="portal-meta">ID: {student.id} • {student.campus} • {student.level}</div>
            </div>
            <div className="ms-auto">
              <button className="btn btn-light btn-sm" onClick={onExportSOA}>Download SOA (PDF)</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row g-3 mt-3">
          <div className="col-12 col-md-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-person-badge" />
                  <div className="fw-bold">Profile</div>
                </div>
                <div className="small">
                  <div><span className="text-muted">Name:</span> {student.name}</div>
                  <div><span className="text-muted">ID:</span> {student.id}</div>
                  <div className="text-capitalize"><span className="text-muted">Campus:</span> {student.campus}</div>
                  <div><span className="text-muted">Grade/Strand:</span> {student.level}</div>
                  {student.email && <div><span className="text-muted">Email:</span> {student.email}</div>}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-8">
            <div className="row g-3">
              <div className="col-6 col-md-3">
                <div className="card portal-card">
                  <div className="card-body">
                    <div className="card-label">Total Due</div>
                    <div className="card-value">₱{Number(student.totalDue || 0).toLocaleString()}</div>
                    <div className="card-note">Payment Mode: {student.paymentMode}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card portal-card">
                  <div className="card-body">
                    <div className="card-label">Total Paid</div>
                    <div className="card-value text-success">₱{Number(student.totalPaid || 0).toLocaleString()}</div>
                    <div className="card-note">Updated in real time</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card portal-card">
                  <div className="card-body">
                    <div className="card-label">Outstanding</div>
                    <div className="card-value text-danger">₱{Number(student.outstanding || 0).toLocaleString()}</div>
                    <div className="card-note">Balance to settle</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card portal-card">
                  <div className="card-body">
                    <div className="card-label">Next Due</div>
                    <div className="card-value">₱{Number(student.nextDue || 0).toLocaleString()}</div>
                    <div className="card-note">{student.nextLabel || ""}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12 col-lg-7">
            <div className="card">
              <div className="card-header bg-light fw-bold">Payment History</div>
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-end">Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(student.payments || []).length === 0 ? (
                      <tr><td colSpan="4" className="text-center text-muted">No payments yet.</td></tr>
                    ) : (
                      (student.payments || []).map(p => (
                        <tr key={`${p.reference}-${p.date}`}>
                          <td>{p.date}</td>
                          <td className="text-end">₱{Number(p.amount || 0).toLocaleString()}</td>
                          <td>{p.method}</td>
                          <td>{p.reference}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <div className="card">
              <div className="card-header bg-light fw-bold">Announcements</div>
              <div className="list-group list-group-flush">
                {announcements.length === 0 ? (
                  <div className="list-group-item text-muted">No announcements.</div>
                ) : (
                  announcements.map(a => (
                    <div key={a.id} className="list-group-item">
                      <div className="d-flex align-items-center gap-2">
                        <div className="fw-bold">{a.title}</div>
                        {a.priority === "high" && <span className="badge bg-danger">High</span>}
                        {a.pinned && <span className="badge bg-primary">Pinned</span>}
                      </div>
                      <div className="small text-muted">{a.publishedAt || a.startsAt || ""}</div>
                      <div>{a.message || a.body}</div>
                      {a.imageUrl && (
                        <div className="mt-2">
                          <img src={a.imageUrl} alt={a.title || "Announcement"} style={{ maxWidth: "100%", borderRadius: 6 }} />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12 col-lg-7">
            <div className="card">
              <div className="card-header bg-light fw-bold">Class Schedule</div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Time</th>
                      <th>Subject</th>
                      <th>Teacher</th>
                      <th>Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleRows.length === 0 ? (
                      <tr><td colSpan="5" className="text-center text-muted">No schedule posted.</td></tr>
                    ) : (
                      scheduleRows.map((r) => (
                          <tr key={`${r.day || ""}|${r.start || ""}|${r.end || ""}|${r.subject || r.name || ""}|${r.teacher || ""}|${r.room || ""}`}>
                            <td>{r.day || ""}</td>
                            <td>{`${r.start || ""}${r.end ? "–" + r.end : ""}`}</td>
                            <td>{r.subject || r.name || ""}</td>
                            <td>{r.teacher || ""}</td>
                            <td>{r.room || ""}</td>
                          </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <div className="card">
              <div className="card-header bg-light fw-bold">Subjects & Grades</div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectRows.length === 0 ? (
                      <tr><td colSpan="2" className="text-center text-muted">No grades yet.</td></tr>
                    ) : (
                      subjectRows.map(r => (
                        <tr key={normKey(r.subject)}>
                          <td>
                            <div className="d-flex justify-content-between align-items-center gap-2">
                              <span>{r.subject}</span>
                              {String(r.gradebookUrl || "").trim() && (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => setActiveGradebook({ url: String(r.gradebookUrl || "").trim(), subject: r.subject })}
                                >
                                  View
                                </button>
                              )}
                            </div>
                          </td>
                          <td>{r.grade ?? "N/A"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {modalOpen && (
        <div
          className="portal-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeGradebook();
          }}
        >
          <div className="portal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="portal-modal-header">
              <div className="portal-modal-title">
                {activeGradebook.subject ? `Gradebook — ${activeGradebook.subject}` : "Gradebook"}
              </div>
              <div className="portal-modal-actions">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeGradebook}>Close</button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => window.open(activeGradebook.url, "_blank", "noopener,noreferrer")}
                >
                  Open
                </button>
              </div>
            </div>
            <div className="portal-modal-body">
              <iframe title="Gradebook" src={toEmbedUrl(activeGradebook.url)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
